import React from 'react';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import { useSetting } from '../../hooks/useSetting';
import { Container } from '../ui';
import '../../styles/rich-text.css';
import CtaBanner from './CtaBanner';

/**
 * Shared template for the privacy policy and the terms.
 *
 * Neither page has a Figma frame, and neither needs one: the body is HTML
 * written in the admin panel, so all the design work is the reading column
 * around it.
 *
 * This replaces two 37-line copies that each carried the same four faults:
 * the language was captured once at module load so switching it changed
 * nothing until a reload; the column was pinned to 50vw and unreadable on a
 * phone; the HTML went in through dangerouslySetInnerHTML; and a failed
 * request left a silent blank page.
 *
 * @param {string} settingKey  Backend key, from SETTING_KEYS.
 * @param {string} title       Already translated.
 */
const LegalPage = ({ settingKey, title }) => {
    const { t } = useTranslation();
    const { html, loading, error } = useSetting(settingKey);

    return (
        <>
            <Helmet><title>{title}</title></Helmet>

            {/* No tinted band and no card: the title and the document share one
                centred column on the page ground, the way the old page read. */}
            <Container as="main" className="tw-pt-12 tw-pb-16 md:tw-pt-20 md:tw-pb-24">
                <header className="tw-mx-auto tw-mb-10 tw-flex tw-max-w-[860px] tw-flex-col tw-items-center tw-gap-5 tw-text-center md:tw-mb-14">
                    <h1 className="tw-text-3xl md:tw-text-v2-h3 tw-font-bold tw-text-v2-text-heading tw-text-balance">
                        {title}
                    </h1>
                    <span className="tw-h-1 tw-w-16 tw-rounded-full tw-bg-v2-brand" aria-hidden />
                </header>

                <div className="tw-mx-auto tw-max-w-[860px]">
                    {loading && <LegalPageSkeleton />}

                    {!loading && error && (
                        <p className="tw-text-v2-base tw-text-ERROR_100">{error}</p>
                    )}

                    {!loading && !error && !html && (
                        <p className="tw-text-v2-base tw-text-v2-text-secondary">
                            {t('v2.legal.empty')}
                        </p>
                    )}

                    {/* The admin panel emits bare tags with no classes, so the
                        typography is applied from here to the parsed tree.
                        Both halves of the class are needed: `v2-rich-text` sets
                        that typography, and `--legal` is what outranks the old
                        design these two documents carry inline on every tag —
                        see rich-text.css. */}
                    {!loading && !error && html && (
                        <div className="v2-rich-text v2-rich-text--legal">
                            {parse(html)}
                        </div>
                    )}
                </div>
            </Container>

            <CtaBanner />
        </>
    );
};

/** Grey bars in the shape of the text, so the layout does not jump on arrival. */
const LegalPageSkeleton = () => (
    <div className="tw-flex tw-flex-col tw-gap-4" aria-hidden>
        {[..."123456"].map((_, index) => (
            <div
                key={index}
                className="tw-h-4 tw-animate-pulse tw-rounded tw-bg-v2-ground-500"
                style={{ width: `${[100, 92, 96, 70, 88, 60][index]}%` }}
            />
        ))}
    </div>
);

export default LegalPage;
